import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../contexts/AuthContext'
import { LoginCredentials, AuthChallengeResponse } from '../types/auth'
import { cognitoService } from '../services/cognitoService'
import { authService } from '../services/authService'
import { viteEnv } from '../utils/viteEnv'
import './Login.css'

// Check if Cognito is configured
const USE_COGNITO = !!(viteEnv.VITE_COGNITO_USER_POOL_ID && viteEnv.VITE_COGNITO_CLIENT_ID)

const loginSchema = z.object({
  // This field represents the login identifier and can be either
  // an email address or a username. We keep the underlying field
  // name as `email` to avoid broad type changes, but relax the
  // validation to allow non-email usernames as well.
  email: z
    .string()
    .min(1, 'Email or username is required'),
  password: z.string().min(1, 'Password is required'),
})

const forgotPasswordSchema = z.object({
  username: z.string().min(1, 'Username or email is required'),
})

const otpSchema = z.object({
  code: z.string().min(6, 'Verification code is required'),
  trustDevice: z.boolean().optional(),
})

const confirmPasswordSchema = z.object({
  code: z.string().min(6, 'Verification code is required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(8, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, completeOtpChallenge, isAuthenticated, isLoading: authLoading } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [pendingChallenge, setPendingChallenge] = useState<AuthChallengeResponse | null>(null)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordUsername, setForgotPasswordUsername] = useState('')
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null)
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
  const [verificationCodeSent, setVerificationCodeSent] = useState(false)
  
  // Check localStorage as fallback if React state hasn't updated yet
  const userFromStorage = authService.getCurrentUser()
  const actuallyAuthenticated = isAuthenticated || !!userFromStorage
  
  // Redirect if already authenticated
  useEffect(() => {
    if (actuallyAuthenticated && !authLoading) {
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }, [actuallyAuthenticated, authLoading, navigate, location])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
  })

  const forgotPasswordForm = useForm<{ username: string }>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const otpForm = useForm<{ code: string; trustDevice?: boolean }>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      trustDevice: false,
    },
  })

  const confirmPasswordForm = useForm<{ code: string; newPassword: string; confirmPassword: string }>({
    resolver: zodResolver(confirmPasswordSchema),
  })

  // Show loading while checking authentication or if already authenticated (will redirect)
  if (authLoading || actuallyAuthenticated) {
    return (
      <div className="login-page" role="main">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  const onSubmit = async (data: LoginCredentials) => {
    setError(null)
    setIsLoading(true)

    try {
      const result = await login(data)
      if (result.status === 'authenticated') {
        const from = (location.state as any)?.from?.pathname || '/dashboard'
        navigate(from, { replace: true })
        return
      }

      setPendingChallenge(result)
      otpForm.reset({
        code: '',
        trustDevice: false,
      })
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtp = async (data: { code: string; trustDevice?: boolean }) => {
    if (!pendingChallenge) {
      setError('Verification session expired. Please sign in again.')
      return
    }

    setError(null)
    setIsLoading(true)
    try {
      await completeOtpChallenge({
        session: pendingChallenge.session,
        username: pendingChallenge.username,
        code: data.code,
        trustDevice: !!data.trustDevice,
        deviceKey: pendingChallenge.deviceKey,
        deviceGroupKey: pendingChallenge.deviceGroupKey,
      })
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const resetOtpStep = () => {
    setPendingChallenge(null)
    otpForm.reset({
      code: '',
      trustDevice: false,
    })
  }

  const handleForgotPassword = async (data: { username: string }) => {
    if (!USE_COGNITO) {
      setForgotPasswordError('Password reset is only available with Cognito authentication')
      return
    }

    setForgotPasswordError(null)
    setForgotPasswordLoading(true)

    try {
      await cognitoService.forgotPassword(data.username)
      setForgotPasswordUsername(data.username)
      setVerificationCodeSent(true)
      setForgotPasswordSuccess(true)
    } catch (err: any) {
      setForgotPasswordError(err.message || 'Failed to send verification code. Please try again.')
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  const handleConfirmPassword = async (data: { code: string; newPassword: string; confirmPassword: string }) => {
    if (!USE_COGNITO) {
      setForgotPasswordError('Password reset is only available with Cognito authentication')
      return
    }

    setForgotPasswordError(null)
    setForgotPasswordLoading(true)

    try {
      await cognitoService.confirmForgotPassword(forgotPasswordUsername, data.code, data.newPassword)
      setForgotPasswordSuccess(true)
      setForgotPasswordError(null)
      // Close forgot password and show success message
      setTimeout(() => {
        setShowForgotPassword(false)
        setVerificationCodeSent(false)
        setForgotPasswordSuccess(false)
        setForgotPasswordUsername('')
        forgotPasswordForm.reset()
        confirmPasswordForm.reset()
        setError(null)
      }, 2000)
    } catch (err: any) {
      setForgotPasswordError(err.message || 'Failed to reset password. Please check your verification code and try again.')
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  const resetForgotPassword = () => {
    setShowForgotPassword(false)
    setVerificationCodeSent(false)
    setForgotPasswordSuccess(false)
    setForgotPasswordError(null)
    setForgotPasswordUsername('')
    forgotPasswordForm.reset()
    confirmPasswordForm.reset()
  }

  return (
    <div className="login-page" role="main">
      <div className="login-container">
        <div className="login-header">
          <div className="logo-large">
            <h1>Clnicl</h1>
            <p className="tagline">Patient Management System</p>
          </div>
        </div>

        <div className="login-form-container">
          <h2>{pendingChallenge ? 'Verify Login' : 'Sign In'}</h2>
          <p className="login-subtitle">
            {pendingChallenge
              ? `Enter the verification code sent to ${pendingChallenge.destination || 'your registered email'}.`
              : 'Enter your credentials to access your account'}
          </p>

          {error && (
            <div className="error-message" role="alert" aria-live="assertive">
              {error}
            </div>
          )}

          {!pendingChallenge ? (
            <form onSubmit={handleSubmit(onSubmit)} className="login-form" noValidate>
              <div className="form-group">
                <label htmlFor="email">
                  Email or Username <span className="required" aria-label="Required">*</span>
                </label>
                <input
                  id="email"
                  type="text"
                  {...register('email')}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter your email or username"
                  autoComplete="username"
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <span id="email-error" className="error-text" role="alert">
                    {errors.email.message}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  Password <span className="required" aria-label="Required">*</span>
                </label>
                <input
                  id="password"
                  type="password"
                  {...register('password')}
                  className={errors.password ? 'error' : ''}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  aria-required="true"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                {errors.password && (
                  <span id="password-error" className="error-text" role="alert">
                    {errors.password.message}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="btn-login"
                disabled={isLoading}
                aria-label="Sign in"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={otpForm.handleSubmit(handleVerifyOtp)} className="login-form" noValidate>
              <div className="form-group">
                <label htmlFor="otp-code">
                  Verification Code <span className="required" aria-label="Required">*</span>
                </label>
                <input
                  id="otp-code"
                  type="text"
                  {...otpForm.register('code')}
                  className={otpForm.formState.errors.code ? 'error' : ''}
                  placeholder="Enter 6-digit code"
                  autoComplete="one-time-code"
                  aria-required="true"
                  aria-invalid={!!otpForm.formState.errors.code}
                  aria-describedby={otpForm.formState.errors.code ? 'otp-code-error' : undefined}
                />
                {otpForm.formState.errors.code && (
                  <span id="otp-code-error" className="error-text" role="alert">
                    {String(otpForm.formState.errors.code.message || 'Verification code is required')}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="btn-login"
                disabled={isLoading}
                aria-label="Verify code"
              >
                {isLoading ? 'Verifying...' : 'Verify and Sign In'}
              </button>
              <button
                type="button"
                className="btn-login"
                onClick={resetOtpStep}
                disabled={isLoading}
                aria-label="Back to sign in"
              >
                Back to Sign In
              </button>
            </form>
          )}

          {!USE_COGNITO && !pendingChallenge && (
            <div className="test-users-info">
              <h3>Test Users</h3>
              <div className="test-users-list">
                <div className="test-user">
                  <strong>Admin:</strong> admin@clnicl.ie / admin123
                </div>
                <div className="test-user">
                  <strong>Doctor:</strong> doctor@clnicl.ie / doctor123
                </div>
                <div className="test-user">
                  <strong>Nurse:</strong> nurse@clnicl.ie / nurse123
                </div>
                <div className="test-user">
                  <strong>Reception:</strong> reception@clnicl.ie / reception123
                </div>
              </div>
            </div>
          )}

          {showForgotPassword && !pendingChallenge && (
            <div className="forgot-password-container">
              <h3>Reset Password</h3>
              
              {!verificationCodeSent ? (
                <>
                  <p>Enter your username or email to receive a verification code.</p>
                  
                  {forgotPasswordError && (
                    <div className="error-message" role="alert">
                      {forgotPasswordError}
                    </div>
                  )}

                  <form onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)} className="forgot-password-form">
                    <div className="form-group">
                      <label htmlFor="forgot-username">
                        Username or Email <span className="required">*</span>
                      </label>
                      <input
                        id="forgot-username"
                        type="text"
                        {...forgotPasswordForm.register('username')}
                        className={forgotPasswordForm.formState.errors.username ? 'error' : ''}
                        placeholder="Enter your username or email"
                      />
                      {forgotPasswordForm.formState.errors.username && (
                        <span className="error-text">
                          {String(forgotPasswordForm.formState.errors.username.message || 'Username or email is required')}
                        </span>
                      )}
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={resetForgotPassword}
                        disabled={forgotPasswordLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={forgotPasswordLoading}
                      >
                        {forgotPasswordLoading ? 'Sending...' : 'Send Verification Code'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  {forgotPasswordSuccess && !forgotPasswordForm.formState.isSubmitSuccessful && (
                    <div className="success-message" role="alert">
                      Verification code sent! Please check your email.
                    </div>
                  )}

                  {forgotPasswordError && (
                    <div className="error-message" role="alert">
                      {forgotPasswordError}
                    </div>
                  )}

                  {confirmPasswordForm.formState.isSubmitSuccessful && (
                    <div className="success-message" role="alert">
                      Password reset successfully! You can now log in.
                    </div>
                  )}

                  <form onSubmit={confirmPasswordForm.handleSubmit(handleConfirmPassword)} className="forgot-password-form">
                    <div className="form-group">
                      <label htmlFor="verification-code">
                        Verification Code <span className="required">*</span>
                      </label>
                      <input
                        id="verification-code"
                        type="text"
                        {...confirmPasswordForm.register('code')}
                        className={confirmPasswordForm.formState.errors.code ? 'error' : ''}
                        placeholder="Enter verification code from email"
                      />
                      {confirmPasswordForm.formState.errors.code && (
                        <span className="error-text">
                          {String(confirmPasswordForm.formState.errors.code.message || 'Verification code is required')}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="new-password">
                        New Password <span className="required">*</span>
                      </label>
                      <input
                        id="new-password"
                        type="password"
                        {...confirmPasswordForm.register('newPassword')}
                        className={confirmPasswordForm.formState.errors.newPassword ? 'error' : ''}
                        placeholder="Enter new password (min 8 characters)"
                      />
                      {confirmPasswordForm.formState.errors.newPassword && (
                        <span className="error-text">
                          {String(confirmPasswordForm.formState.errors.newPassword.message || 'Invalid password')}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirm-password">
                        Confirm Password <span className="required">*</span>
                      </label>
                      <input
                        id="confirm-password"
                        type="password"
                        {...confirmPasswordForm.register('confirmPassword')}
                        className={confirmPasswordForm.formState.errors.confirmPassword ? 'error' : ''}
                        placeholder="Confirm new password"
                      />
                      {confirmPasswordForm.formState.errors.confirmPassword && (
                        <span className="error-text">
                          {String(confirmPasswordForm.formState.errors.confirmPassword.message || 'Passwords do not match')}
                        </span>
                      )}
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={resetForgotPassword}
                        disabled={forgotPasswordLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={forgotPasswordLoading}
                      >
                        {forgotPasswordLoading ? 'Resetting...' : 'Reset Password'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}
        </div>

        <div className="login-footer">
          <p className="legal-links">
            <a href="/terms-and-conditions">Terms</a> | <a href="/privacy-notice">Privacy</a> |{' '}
            <a href="/pcrs-responsibility">PCRS Statement</a>
          </p>
          {USE_COGNITO && (
            <p>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setShowForgotPassword(!showForgotPassword)
                  setError(null)
                  if (pendingChallenge) {
                    resetOtpStep()
                  }
                }}
              >
                {showForgotPassword ? 'Back to login' : 'Forgot password?'}
              </a>
            </p>
          )}
          {!USE_COGNITO && (
            <>
              <p>
                <a href="#" onClick={(e) => e.preventDefault()}>
                  Forgot password?
                </a>
              </p>
              <p className="footer-note">
                This is a test environment. In production, authentication will be handled by AWS Cognito.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}














